import React, { useState, useEffect, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { Button } from '@/components/ui/button'
import { Upload, X, LoaderCircle } from 'lucide-react'

const formatTimeRemaining = (seconds) => {
  if (!seconds || !isFinite(seconds) || seconds <= 0) return null
  if (seconds < 60) return `${Math.ceil(seconds)}s remaining`
  const mins = Math.floor(seconds / 60)
  const secs = Math.ceil(seconds % 60)
  return `${mins}m ${secs}s remaining`
}

/**
 * Single file upload progress with time estimate.
 */
const FileProgress = ({ upload }) => {
  const startTimeRef = useRef(null)
  const [eta, setEta] = useState(null)

  useEffect(() => {
    if (upload.progress > 0 && !startTimeRef.current) {
      startTimeRef.current = Date.now()
    }

    if (startTimeRef.current && upload.progress > 0 && upload.progress < 100) {
      const elapsed = (Date.now() - startTimeRef.current) / 1000
      const rate = upload.progress / elapsed
      const remaining = (100 - upload.progress) / rate
      setEta(formatTimeRemaining(remaining))
    } else {
      setEta(null)
    }
  }, [upload.progress])

  return (
    <div className="w-full min-w-0">
      <div className="flex items-center gap-2 text-sm mb-1 w-full min-w-0">
        <p className="flex-1 w-0 truncate font-medium mb-0">{upload.name}</p>
        <span className="text-muted-foreground shrink-0">{upload.progress}%</span>
      </div>
      <Progress value={upload.progress} className="h-2 mb-1" />
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{upload.loaded} / {upload.total}</span>
        {eta && <span className="shrink-0">{eta}</span>}
      </div>
    </div>
  )
}

/**
 * Reusable progress bars for file uploads (no modal wrapper).
 */
export const UploadProgressBars = ({ uploads }) => {
  const keys = Object.keys(uploads || {})
  if (keys.length === 0) return null

  return (
    <div className="space-y-4">
      {keys.map((k) => (
        <FileProgress key={k} upload={uploads[k]} />
      ))}
    </div>
  )
}

/**
 * Upload progress displayed in a standalone modal.
 */
export default ({
  uploads,
  loading,
  open,
  finalizing = false,
  uploadingLabel = 'Uploading...',
  finalizingLabel = 'Finalizing...',
  waitLabel,
  onCancel,
}) => {
  const isOpen = open !== undefined ? open : loading
  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-lg overflow-hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} hideClose>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {finalizing ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 animate-bounce" />
            )}
            {finalizing ? finalizingLabel : uploadingLabel}
          </DialogTitle>
        </DialogHeader>
        {finalizing ? (
          <p className="text-sm text-muted-foreground py-2">{waitLabel || finalizingLabel}</p>
        ) : (
          <UploadProgressBars uploads={uploads} />
        )}
        {onCancel && !finalizing && (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (onCancel) onCancel()
              }}
            >
              <X className="h-4 w-4" />
              Cancel upload
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
